import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Box, Progress, Text, createStyles } from '@mantine/core';
import { IoMdHeadset } from 'react-icons/io';
import { MdOutlineAccountCircle } from 'react-icons/md';

import Participant from './Participant';
import StreamingOffBox from '../StreamingOffBox';
import useUserData from '../../store/useUserStore';

const Room = ({ userProfileId = '', room }) => {
  const { classes } = useStyles();
  const userProfile = useUserData(state => state.userProfile.userProfileData);
  const [participants, setParticipants] = useState([]);

  const avatarImageUrl = userProfile.profileImage ?? '';

  useEffect(() => {
    const addParticipant = participant => {
      setParticipants(currentParticipants => {
        const existingParticipant = currentParticipants.find(
          currentParticipant => currentParticipant.sid === participant.sid
        );

        if (existingParticipant === participant) return currentParticipants;

        if (existingParticipant) {
          return currentParticipants.map(currentParticipant =>
            currentParticipant.sid === participant.sid
              ? participant
              : currentParticipant
          );
        }

        return [...currentParticipants, participant];
      });
    };

    const removeParticipant = participant => {
      setParticipants(currentParticipants =>
        currentParticipants.filter(
          currentParticipant => currentParticipant.sid !== participant.sid
        )
      );
    };

    room.on('participantConnected', addParticipant);
    room.on('participantDisconnected', removeParticipant);
    room.participants.forEach(addParticipant);

    return () => {
      room.off('participantConnected', addParticipant);
      room.off('participantDisconnected', removeParticipant);
    };
  }, [room]);

  const connectedParticipants = useMemo(
    () =>
      participants.filter(participant => participant.state === 'connected'),
    [participants]
  );

  const publisher = connectedParticipants.find(
    participant => participant.identity === userProfileId
  );

  const otherParticipants = connectedParticipants.filter(
    participant => participant.identity !== userProfileId
  );

  const dispatcherCount = otherParticipants.filter(participant =>
    participant.identity.includes('dispatcher')
  ).length;

  const remoteViewerCount = otherParticipants.filter(
    participant => !participant.identity.includes('dispatcher')
  ).length;
  const viewerCount = 1 + remoteViewerCount;

  const participantAvatars = otherParticipants.map(participant =>
    participant.identity.includes('dispatcher') ? (
      <Avatar
        size={30}
        radius={20}
        color="white"
        variant="filled"
        key={participant.sid}>
        <IoMdHeadset size={22} color="#C8453A" />
      </Avatar>
    ) : (
      <Avatar
        size={30}
        radius={20}
        color="gray"
        key={participant.sid}>
        <MdOutlineAccountCircle size={30} />
      </Avatar>
    )
  );

  if (!room) {
    return (
      <div className={classes.containerOffBox}>
        <Box>
          <StreamingOffBox />
        </Box>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {connectedParticipants.length > 0 ? (
        <Box className={classes.otherParticipants}>
          <Avatar.Group spacing="sm">
            <Avatar
              px={0}
              py={0}
              size={30}
              radius={20}
              color="red"
              src={avatarImageUrl}
              className={classes.avatarAnimation}
            />
            {participantAvatars.slice(0, 2)}
            {participantAvatars.length > 2 && (
              <Avatar
                size={30}
                radius={20}
                className={classes.avatarAnimation}>
                <Text
                  weight={500}
                  color="gray"
                  size={12}
                  style={{ whiteSpace: 'nowrap' }}>
                  +{participantAvatars.length - 2}
                </Text>
              </Avatar>
            )}
          </Avatar.Group>
        </Box>
      ) : (
        <Box>
          <Progress
            radius="xs"
            size="sm"
            value={100}
            striped
            animate
            style={{ position: 'absolute', bottom: 0 }}
          />
        </Box>
      )}

      <div className="remote-participants">
        {publisher?.state === 'connected' && (
          <Participant
            key={publisher.sid}
            participant={publisher}
            participantCount={viewerCount}
            dispatchersCount={dispatcherCount}
          />
        )}
      </div>
    </div>
  );
};

export default Room;

const useStyles = createStyles({
  container: {
    position: 'relative',
    backgroundImage: 'radial-gradient(circle at 50% 0, #3c3c3c, #141414 84%)',
    '@media (min-width: 1024px) ': {
      minHeight: 200,
    },
  },
  containerOffBox: {
    position: 'relative',
    backgroundImage: 'radial-gradient(circle at 50% 0, #3c3c3c, #141414 84%)',
    '@media (max-width: 767px) and (orientation: portrait)': {
      height: 0,
    },
  },
  otherParticipants: {
    opacity: 0.4,
    WebkitTransition: '250ms',
    position: 'absolute',
    zIndex: 200,
    left: 20,
    bottom: 25,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',

    '&:hover': {
      opacity: 1,
      WebkitTransition: '250ms',
      boxShadow: '0px 7px 20px 0px rgba(0, 0, 0, 0.07)',
    },

    '@media (min-width: 1024px)': {
      left: 20,
      bottom: 30,
    },
  },
  avatarAnimation: {
    boxShadow: '0px 7px 20px 0px rgba(0, 0, 0, 0.5)',
  },
});
